import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import orientationService from '../../../api/services/orientationService';
import { useCustomAlert } from '../../../hooks/useCustomAlert';
import { usePermissions } from '../../../hooks/usePermissions';
import CustomAlert from '../../../components/common/CustomAlert';
import {
  ArrowLeft,
  Calendar,
  MapPin,
  Clock,
  Users,
  User,
  Building,
  Video,
  Edit,
  Send,
  Trash2,
  CheckCircle,
  AlertCircle,
  Play,
  Mail,
  Building2,
  XCircle,
  FileSpreadsheet
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import { formatDate } from '../../../utils/stringUtils';

const ViewOrientation = ({ id: propId }) => {
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const id = propId || paramId;
  const { user } = useAuth();
  const { hasPermission, canUpdate, canCreate, canDelete } = usePermissions();
  const { alertState, showSuccess, showError, hideAlert } = useCustomAlert();
  const [orientation, setOrientation] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSendingReminders, setIsSendingReminders] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Filter attendees to count only those who accepted
  const acceptedAttendeesCount = Array.isArray(attendees)
    ? attendees.filter(a => a.inviteStatus === 'accepted').length
    : 0;

  const capacityFilled = Math.max(acceptedAttendeesCount, orientation?.attendeesCount || 0);


  useEffect(() => {
    loadOrientation();
    loadAttendees();
  }, [id]);

  // Auto-update status on component mount
  useEffect(() => {
    updateOrientationStatus();
  }, []);

  const loadOrientation = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await orientationService.getOrientationById(id);

      if (result && result.responseCode === 200 && result.data) {
        setOrientation(result.data.orientation);
      } else {
        setError('Orientation not found');
        showError('Orientation not found');
      }
    } catch (error) {
      setError('Error loading orientation');
      showError('Error loading orientation');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAttendees = async () => {
    try {
      const result = await orientationService.getOrientationAttendees(id);
      const attendeesData = result?.data?.attendees || result?.attendees || [];
      setAttendees(attendeesData);
    } catch (error) {
      setAttendees([]);
    }
  };

  const updateOrientationStatus = async () => {
    try {
      const result = await orientationService.updateStatuses();

      if (result && result.responseCode === 200) {
        // Reload orientation to reflect status changes
        await loadOrientation();
      }
    } catch (error) {
      // Don't show error to user for automatic updates
    }
  };

  // Permission-based access control (Dynamic RBAC)
  const canViewOrientation = () => {
    return hasPermission('onboarding', 'read');
  };

  const canEditOrientation = () => {
    return hasPermission('onboarding', 'update');
  };

  const canDeleteOrientation = () => {
    return canDelete('onboarding');
  };

  const canSendInvites = () => {
    return canCreate('onboarding') || hasPermission('onboarding', 'update');
  };

  const canCloneOrientation = () => {
    return canCreate('onboarding');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'ongoing': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'scheduled': return <Calendar className="h-4 w-4" />;
      case 'ongoing': return <Play className="h-4 w-4" />;
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      case 'cancelled': return <AlertCircle className="h-4 w-4" />;
      default: return <Calendar className="h-4 w-4" />;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'in_person': return <MapPin className="h-4 w-4" />;
      case 'virtual': return <Video className="h-4 w-4" />;
      default: return <Calendar className="h-4 w-4" />;
    }
  };

  const formatTimeToAMPM = (timeString) => {
    if (!timeString) return 'N/A';

    try {
      // Handle formats like "14:30" or "14:30:00"
      const timeParts = timeString.split(':');
      if (timeParts.length < 2) return timeString;

      const hours = parseInt(timeParts[0], 10);
      const minutes = timeParts[1];

      if (isNaN(hours) || hours < 0 || hours > 23) return timeString;

      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;

      return `${displayHours}:${minutes} ${period}`;
    } catch (error) {
      return timeString;
    }
  };

  const handleEdit = () => {
    navigate(`/create_orientation/${id}`);
  };

  const handleDelete = () => {
    if (!orientation) {
      showError('Orientation data not available');
      return;
    }

    // Check if orientation can be deleted
    if (orientation.status !== 'scheduled') {
      showError('Only scheduled orientations can be deleted');
      return;
    }

    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      const result = await orientationService.deleteOrientation(id);

      if (result && result.responseCode === 200) {
        showSuccess('Orientation deleted successfully');
        setShowDeleteModal(false);
        navigate('/orientation');
      } else {
        showError(result?.responseMessage || 'Failed to delete orientation');
      }
    } catch (error) {
      showError('An error occurred while deleting the orientation');
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handleSendInvites = () => {
    navigate(`/send_orientation_invites/${id}`);
  };

  const handleSendReminders = async () => {
    setIsSendingReminders(true);

    // Use toast.promise for real-time toaster notification
    const reminderPromise = orientationService.sendOrientationReminders(id);

    toast.promise(reminderPromise, {
      loading: 'Sending reminders to all invited candidates...',
      success: (result) => {
        setIsSendingReminders(false);
        return result.responseMessage || 'Reminders sent successfully!';
      },
      error: (err) => {
        setIsSendingReminders(false);
        return err.response?.data?.responseMessage || err.message || 'Failed to send reminders';
      }
    });

    try {
      await reminderPromise;
    } catch (error) {
      // Error sending reminders
    }
  };

  const handleClone = () => {
    navigate(`/clone_orientation/${id}`);
  };

  const handleExport = async () => {
    if (isExporting || !orientation) return;
    setIsExporting(true);
    try {
      const exportName = orientation.title || 'Orientation';
      const result = await orientationService.exportOrientationDetail(id, exportName);
      if (result.success) {
        showSuccess('Orientation details exported successfully');
      } else {
        showError(result.error || 'Failed to export orientation details');
      }
    } catch (err) {
      showError(err.message || 'Failed to export orientation details');
    } finally {
      setIsExporting(false);
    }
  };



  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-agrosaf-green mx-auto mb-4"></div>
          <p className="text-gray-600">Loading orientation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">❌</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Orientation</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <PermissionGate permission="onboarding" action="read">
            <Button onClick={loadOrientation} className="bg-agrosaf-green hover:bg-agrosaf-darkGreen">
              Try Again
            </Button>
          </PermissionGate>
        </div>
      </div>
    );
  }

  if (!canViewOrientation()) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to view orientation sessions.</p>
        </div>
      </div>
    );
  }

  if (!orientation) {
    return (
      <div className="text-center p-8">
        <h2 className="text-2xl font-bold text-red-600">Orientation Not Found</h2>
        <p className="text-gray-600 mt-2">The requested orientation session could not be found.</p>
        <PermissionGate permission="onboarding" action="read">
          <Button onClick={() => navigate('/orientation')} className="mt-4">
            Back to Orientations
          </Button>
        </PermissionGate>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center space-x-4 min-w-0">
          <Button
            variant="outline"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-bold text-gray-900 capitalize break-words overflow-hidden">
              {orientation.title?.replace(/\b\w/g, l => l.toUpperCase())}
            </h1>
            <p className="text-gray-600 mt-1">Orientation Session Details</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(orientation.status)}`}>
            {getStatusIcon(orientation.status)}
            <span className="ml-2 capitalize">{orientation.status}</span>
          </span>
          <Button
            variant="outline"
            icon={<FileSpreadsheet className="h-4 w-4" />}
            onClick={handleExport}
            loading={isExporting}
            disabled={isExporting}
          >
            {isExporting ? 'Exporting...' : 'Export'}
          </Button>
          {canEditOrientation() && orientation.status !== 'completed' && orientation.status !== 'cancelled' && (
            <Button
              variant="outline"
              icon={<Edit className="h-4 w-4" />}
              onClick={handleEdit}
            >
              Edit
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-6">
        {/* Status and Actions at Top */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Status Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Status</h3>
            <div className="text-center">
              <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(orientation.status)} mb-3`}>
                {getStatusIcon(orientation.status)}
                <span className="ml-2 capitalize">{orientation.status}</span>
              </div>
              <div className="text-sm text-gray-600">
                <p>Duration: {orientation.duration === 0.5 ? '30 min' : `${orientation.duration} ${orientation.duration === 1 ? 'hour' : 'hours'}`}</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>
            <div className="space-y-3">
              {canSendInvites() && orientation.status === 'scheduled' && (
                <Button
                  variant="outline"
                  icon={<Send className="h-4 w-4" />}
                  onClick={handleSendInvites}
                  className="w-full"
                >
                  Send Invites
                </Button>
              )}
              {canSendInvites() && orientation.status === 'scheduled' && attendees.length > 0 && (
                <Button
                  variant="outline"
                  icon={<Mail className="h-4 w-4" />}
                  onClick={handleSendReminders}
                  className="w-full border-blue-200 text-blue-600 hover:bg-blue-50"
                  isLoading={isSendingReminders}
                >
                  Send Reminders
                </Button>
              )}
              {canDeleteOrientation() && orientation.status === 'scheduled' && (
                <Button
                  variant="outline"
                  icon={<Trash2 className="h-4 w-4" />}
                  onClick={handleDelete}
                  className="w-full text-red-600 hover:text-red-700"
                >
                  Delete Session
                </Button>
              )}
              <Button
                variant="outline"
                icon={<FileSpreadsheet className="h-4 w-4" />}
                className="w-full"
                onClick={handleExport}
                loading={isExporting}
                disabled={isExporting}
              >
                {isExporting ? 'Exporting...' : 'Export to Excel'}
              </Button>

            </div>
          </div>
        </div>

        {/* Session Details */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Session Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-3">
              <Calendar className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm text-gray-600">Date & Time</p>
                <p className="font-medium text-gray-900 capitalize break-words overflow-hidden">{formatDate(orientation.date, { includeTime: false }) || 'N/A'} at {formatTimeToAMPM(orientation.time)}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">{getTypeIcon(orientation.type)}</div>
              <div className="min-w-0">
                <p className="text-sm text-gray-600">Type</p>
                <p className="font-medium text-gray-900 capitalize break-words overflow-hidden">{orientation.type.replace('_', ' ')}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              {orientation.type === 'virtual' ? (
                <Video className="h-5 w-5 text-gray-400 mt-1" />
              ) : (
                <MapPin className="h-5 w-5 text-gray-400 mt-1" />
              )}
              <div className="min-w-0">
                <p className="text-sm text-gray-600">Location</p>
                <p className="font-medium text-gray-900 capitalize whitespace-pre-wrap break-words">
                  {typeof orientation.location === 'object' && orientation.location !== null
                    ? orientation.location.name
                    : orientation.location}
                </p>
                {typeof orientation.location === 'object' && orientation.location !== null && orientation.location.fullAddress && (
                  <p className="text-sm text-gray-500 mt-1 capitalize whitespace-pre-wrap break-words">
                    {orientation.location.fullAddress}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Clock className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm text-gray-600">Duration</p>
                <p className="font-medium text-gray-900 capitalize break-words overflow-hidden">{orientation.duration === 0.5 ? '30 min' : `${orientation.duration} ${orientation.duration === 1 ? 'hour' : 'hours'}`}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Users className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm text-gray-600">Capacity</p>
                <p className="font-medium text-gray-900 capitalize break-words overflow-hidden">{orientation.capacity}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <User className="h-5 w-5 text-gray-400 flex-shrink-0 mt-1" />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-600 mb-2">Trainer{orientation.trainerData?.length > 1 ? 's' : ''}</p>
                {orientation.trainerData && orientation.trainerData.length > 0 ? (
                  <div className="space-y-2">
                    {orientation.trainerData.map((trainer, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <p className="font-medium text-gray-900 capitalize">{trainer.name}</p>
                        <p className="text-sm text-gray-600 mt-1">
                          <span className="inline-flex items-center gap-1">
                            <Mail className="h-3.5 w-3.5" /> {trainer.email}
                          </span>
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          <span className="inline-flex items-center gap-1">
                            <Building2 className="h-3.5 w-3.5" /> {trainer.department}
                          </span>
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="font-medium text-gray-900 capitalize break-words overflow-hidden">
                    {Array.isArray(orientation.trainer) ? orientation.trainer.join(', ') : orientation.trainer}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Building className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-600">Department</p>
                <p className="font-medium text-gray-900 capitalize break-words overflow-hidden">{orientation.department}</p>
              </div>
            </div>
          </div>
          {orientation.description && (
            <div className="mt-4">
              <p className="text-sm text-gray-600">Description</p>
              <p className="text-gray-900 capitalize whitespace-pre-wrap break-words">{orientation.description}</p>
            </div>
          )}
        </div>

        {/* Agenda */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Agenda</h3>
            {orientation.agenda?.length > 0 && (
              <span className="text-sm text-gray-500">
                {orientation.agenda.length} item{orientation.agenda.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="space-y-4">
            {orientation.agenda && orientation.agenda.length > 0 ? (
              orientation.agenda.map((item, index) => (
                <div
                  key={item._id || index}
                  className="relative p-4 bg-gradient-to-r from-gray-50 to-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                    <div className="flex items-center">
                      <div className="flex items-center justify-center h-10 w-10 rounded-full bg-agrosaf-green/10 text-agrosaf-green font-semibold">
                        #{index + 1}
                      </div>
                      <div className="h-10 w-1 rounded-full bg-gradient-to-b from-agrosaf-green to-transparent ml-4 sm:ml-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-semibold text-gray-900 capitalize break-words overflow-hidden flex-1 min-w-0">
                          {item.title?.replace(/\b\w/g, l => l.toUpperCase())}
                        </p>
                        <span className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-agrosaf-green/10 text-agrosaf-green whitespace-nowrap flex-shrink-0">
                          {item.duration || 0} min
                        </span>
                      </div>
                      {item.description && (
                        <p className="mt-2 text-sm text-gray-600 capitalize break-words overflow-hidden">
                          {item.description}
                        </p>
                      )}
                      {(item.speaker || item.topic) && (
                        <div className="mt-3 flex flex-wrap gap-2 text-xs text-gray-500">
                          {item.topic && (
                            <span className="inline-flex items-center px-2 py-1 bg-gray-100 rounded-lg capitalize">
                              Topic: {item.topic}
                            </span>
                          )}
                          {item.speaker && (
                            <span className="inline-flex items-center px-2 py-1 bg-gray-100 rounded-lg capitalize">
                              Speaker: {item.speaker}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-4">No agenda items available</p>
            )}
          </div>
        </div>

        {/* Invited Candidates */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Invited Candidates ({attendees.length})</h3>
          <div className="space-y-3">
            {attendees.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {attendees.map((att) => {
                  let statusColor = 'bg-gray-50 border-gray-200';
                  let icon = <Clock className="h-5 w-5 text-gray-400 flex-shrink-0" />;
                  let statusText = 'Pending';

                  if (att.inviteStatus === 'accepted') {
                    statusColor = 'bg-green-50 border-green-200';
                    icon = <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />;
                    statusText = 'Accepted';
                  } else if (att.inviteStatus === 'declined') {
                    statusColor = 'bg-red-50 border-red-200';
                    icon = <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />;
                    statusText = 'Declined';
                  }

                  return (
                    <div key={att.id || att.email} className="flex items-center justify-between p-3 rounded-lg border bg-white border-gray-200">
                      <div className="flex items-center space-x-3 min-w-0 flex-1">
                        <User className="h-5 w-5 text-gray-400 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-gray-900 capitalize break-words overflow-hidden" title={att.name}>{att.name || 'Candidate'}</p>
                          <p className="text-sm text-gray-500 truncate" title={att.email}>{att.email}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No candidates invited yet.</p>
            )}
          </div>
        </div>
      </div>

      {/* Custom Alert */}
      <CustomAlert
        alertState={alertState}
        hideAlert={hideAlert}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">Delete Orientation</h3>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-600">
                Are you sure you want to delete the orientation "{orientation?.title}"? This action cannot be undone.
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={cancelDelete}
                disabled={isDeleting}
                className="px-4 py-2"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={confirmDelete}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewOrientation;
